// Shared workspace split tools: a column resizer handle plus a media-query hook.
// Panels stay pure CSS grids; the handle only writes a `--side-w` custom property
// (persisted by the caller in localStorage), so nothing is locked in markup.
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { GripVertical } from 'lucide-react';

/** useState mirrored from a CSS media query (for JS-level responsive behaviour). */
export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => (typeof window === 'undefined' ? false : window.matchMedia(query).matches));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return matches;
}

interface ColResizerProps {
  /** Current side-column width in px; 0 means "not resized yet" (CSS default applies). */
  width: number;
  onWidth(px: number): void;
  /** Double-click / reset affordance back to the CSS default. */
  onReset(): void;
  label: string;
  min?: number;
  max?: number;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/**
 * Drag handle for the grid column preceding it (`previousElementSibling`).
 * `role="separator"` keeps it operable from the keyboard; hidden on stacked
 * layouts through CSS (`.col-resizer { display: none }`).
 */
export function ColResizer({ width, onWidth, onReset, label, min = 240, max = 900 }: ColResizerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startX: number; startW: number } | null>(null);

  const currentWidth = useCallback(() => {
    const el = ref.current?.previousElementSibling as HTMLElement | null;
    return el?.getBoundingClientRect().width ?? min;
  }, [min]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    drag.current = { startX: e.clientX, startW: currentWidth() };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.currentTarget.style.cursor = 'col-resize';
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    onWidth(clamp(Math.round(drag.current.startW + e.clientX - drag.current.startX), min, max));
  };
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    e.currentTarget.style.cursor = '';
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 48 : 16;
    const base = drag.current?.startW ?? (width || currentWidth());
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onWidth(clamp(Math.round(base - step), min, max));
      drag.current = null;
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onWidth(clamp(Math.round(base + step), min, max));
      drag.current = null;
    } else if (e.key === 'Home') {
      e.preventDefault();
      onReset();
    }
  };

  return (
    <div
      ref={ref}
      className="col-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width || undefined}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      title={label}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
    >
      <GripVertical className="grip" />
    </div>
  );
}
